<template>
  <div
    v-if="suggestedMarket"
    class="fixed z-modalsContent bottom-0 right-0 desk:bottom-24 desk:right-24 w-full desk:w-410 bg-lightest shadow-lg"
  >
    <div class="p-16 desk:p-24">
      <div class="flex items-start justify-between mb-16">
        <div class="flex items-center gap-8">
          <img
            :src="suggestedMarket.flagUrl"
            alt=""
            class="w-24 h-24 rounded-full object-cover"
          >
          <span class="type-headline-sm uppercase">{{ countryName }}</span>
        </div>
        <button
          type="button"
          class="shrink-0"
          @click="stay"
        >
          <img
            src="/icons/close.svg"
            class="w-16 h-16"
            alt="Close"
          >
        </button>
      </div>

      <div
        v-if="popupCopy"
        class="flex flex-col gap-8"
      >
        <div class="type-headline-base">
          {{ popupTitle }}
        </div>
        <div class="type-sm text-text-subtle">
          {{ popupBody }}
        </div>
        <div class="flex flex-col desk:flex-row gap-8 mt-8">
          <a
            :href="suggestedMarket.url"
            class="flex items-center justify-center h-40 px-16 desk:px-20 type-headline-sm bg-brandPrimary hover:bg-brandDark text-lightest transition-all duration-200 ease-out w-full desk:w-auto cursor-pointer select-none"
            @click="goToMarket"
          >
            {{ popupCopy['marketPopup.cta'] }} {{ marketDomainLabel }}
          </a>
          <button
            type="button"
            class="btn btn--secondary w-full desk:w-auto"
            @click="stay"
          >
            {{ popupCopy['marketPopup.stay'] }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useGlobalContentStore } from '~/store/globalContent';
import { useUiStore } from '~/store/ui';
import { getCountryNativeName } from '~/util/domain-mapper';
import useApiFetch from '~/composables/useApiFetch';

type TranslationsResponse = {
  lang: string,
  translations: Record<string, string>,
};

const globalContentStore = useGlobalContentStore();
const uiStore = useUiStore();
const { apiGet } = useApiFetch();

const suggestedMarket = computed(() => globalContentStore.getSuggestedMarket);
const countryName = computed(() => getCountryNativeName(suggestedMarket.value?.countryCode));
const marketDomainLabel = computed(() => (suggestedMarket.value?.url ?? '').replace(/^https?:\/\/(www\.)?/, ''));

const dismissedCookie = useCookie('kahrs-market-popup-dismissed', {
  maxAge: 60 * 60 * 24 * 365,
  path: '/',
});

const popupCopy = ref<Record<string, string> | null>(null);

const popupTitle = computed(() => {
  const countryCode = suggestedMarket.value?.countryCode;
  return popupCopy.value?.[`marketPopup.title.${countryCode}`] ?? popupCopy.value?.['marketPopup.title'];
});
const popupBody = computed(() => {
  const countryCode = suggestedMarket.value?.countryCode;
  return popupCopy.value?.[`marketPopup.body.${countryCode}`] ?? popupCopy.value?.['marketPopup.body'];
});

// Popup copy is shown in the *target* market's language, not the current site's -
// $t() only resolves the currently loaded bundle, so we fetch the target locale directly
onMounted(async() => {
  if (!suggestedMarket.value?.locale || !suggestedMarket.value?.applicationId) {
    return;
  }
  const res = await apiGet<TranslationsResponse>(
    `/applications/translations?lang=${suggestedMarket.value.locale}`,
    { ApplicationId: String(suggestedMarket.value.applicationId) }
  );
  if (!res) {
    return;
  }

  if (globalContentStore.getEnvironment === 'local') {
    const lang = await import('~/lang/local.json');
    popupCopy.value = { ...lang.default, ...res.translations };
  } else {
    popupCopy.value = res.translations;
  }
});

const goToMarket = () => {
  dismissedCookie.value = 'true';
};

const stay = () => {
  dismissedCookie.value = 'true';
  uiStore.setShowMarketSwitchModal(false);
};
</script>
